import { LogOut, Settings } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSnapshot } from "valtio";
import { dashboardStore, spouseStore, userStore } from "../../hooks/store";
import { UserWeddingRole } from "../../types/wedding";
import { logout } from "../../utils/logout";
import QrCodeIcon from "../icons/QrCodeIcon";

interface TailwindHeaderProps {
  codesCount?: number;
  maxCodes?: number;
}

const TailwindHeader = ({
  codesCount = 0,
  maxCodes = 5,
}: TailwindHeaderProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  // Get user data from stores
  const { data: dashboardData } = useSnapshot(dashboardStore);
  const { data: loggedInUserData } = useSnapshot(userStore);
  const { data: spouseData } = useSnapshot(spouseStore);

  // Profile data
  const shouldDisplayCouple = dashboardData.hasMultiAccounts;
  const loggedInUserFirstName = loggedInUserData.firstname;
  const loggedInUserImage = loggedInUserData.profilePicture;
  const creatorRole = dashboardData.creatorRole;
  const spouseImage = spouseData.profilePicture;
  const groomFirstName = dashboardData.groomFirstName;
  const brideFirstName = dashboardData.brideFirstName;

  // Determine if we should show codes counter based on route
  const isAccessCardFlow = location.pathname.includes("/accesscards");

  // Generate breadcrumbs based on current route
  const getBreadcrumbs = () => {
    if (isAccessCardFlow) {
      return [{ label: "Dashboard", href: "/" }, { label: "Access Cards" }];
    }
    return [];
  };

  const breadcrumbs = getBreadcrumbs();

  const goToSettings = () => {
    navigate("/settings");
    setIsProfileMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    setIsProfileMenuOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target as Node)
      ) {
        setIsProfileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  return (
    <header className="w-full h-[72px] fixed top-0 z-50 bg-gray-50 border-b border-gray-200">
      <div className="max-w-screen-2xl mx-auto px-4 md:px-12 lg:px-8 h-full">
        <div className="flex items-center justify-between h-full">
          {/* Left Section - Logo and Breadcrumbs */}
          <div className="flex items-center">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <div className="w-9 h-9 bg-black rounded-full flex items-center justify-center">
                <span className="text-white text-sm dcalligraphy">W</span>
              </div>
            </Link>

            {/* Breadcrumbs */}
            {breadcrumbs.length > 0 && (
              <nav className="ml-3 hidden md:block">
                <div className="flex items-center space-x-2">
                  {breadcrumbs.map((crumb, index) => (
                    <div key={index} className="flex items-center">
                      {index > 0 && (
                        <span className="mx-2 text-gray-400">|</span>
                      )}
                      {crumb.href ? (
                        <Link
                          to={crumb.href}
                          className="text-sm font-medium text-gray-700 hover:text-gray-900 uppercase tracking-wide"
                        >
                          {crumb.label}
                        </Link>
                      ) : (
                        <span className="text-sm font-bold text-gray-500 uppercase tracking-wide opacity-50">
                          {crumb.label}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </nav>
            )}
          </div>

          {/* Right Section - Codes Counter and Profile */}
          <div className="flex items-center space-x-4">
            {/* Codes Counter - only show in access card flow */}
            {isAccessCardFlow && (
              <div className="flex items-center space-x-2 text-sm text-gray-600 bg-green-50 px-3 py-2 rounded-lg border border-green-100">
                <QrCodeIcon className="w-4 h-4" strokeWidth={1.5} />
                <span className="font-medium tracking-wide">
                  {codesCount}/{maxCodes} CODES
                </span>
              </div>
            )}

            {/* Profile Menu */}
            <div className="relative" ref={profileMenuRef}>
              <button
                onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-300 bg-white hover:bg-gray-50 transition-colors"
              >
                {/* Avatar(s) */}
                <div className="flex -space-x-1">
                  {/* Primary user avatar */}
                  {loggedInUserImage ? (
                    <img
                      src={loggedInUserImage}
                      alt={loggedInUserFirstName}
                      className="w-7 h-7 rounded-full border-2 border-white object-cover"
                    />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-teal-500 flex items-center justify-center text-white text-xs font-medium border-2 border-white">
                      {(creatorRole === UserWeddingRole.Bride
                        ? brideFirstName
                        : groomFirstName
                      )?.charAt(0)}
                    </div>
                  )}

                  {/* Spouse avatar if couple */}
                  {shouldDisplayCouple &&
                    (spouseImage ? (
                      <img
                        src={spouseImage}
                        alt="Spouse"
                        className="w-7 h-7 rounded-full border-2 border-white object-cover"
                      />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-orange-500 flex items-center justify-center text-white text-xs font-medium border-2 border-white">
                        {(creatorRole === UserWeddingRole.Bride
                          ? groomFirstName
                          : brideFirstName
                        )?.charAt(0)}
                      </div>
                    ))}
                </div>
              </button>

              {/* Dropdown Menu */}
              {isProfileMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50">
                  <button
                    onClick={goToSettings}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </button>
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default TailwindHeader;
